// A top-down ship plan (like the printed board) for picking a space: destinations glow and
// can be clicked; characters, Deep Ones and passengers are shown as small markers.
import * as uf from "../../../../common/unfathomable";
import { art } from "./assets";
import { kSpaceGeom, polyCentroid, polyPoints } from "./boardGeometry";
import { kSeatColors } from "./seatColors";
import css from "./Unfathomable.module.css";

const kView = { x: 0, y: 560, w: 2000, h: 790 };

export function MiniMap({ view, artOk, targets, onPick }: {
  view: uf.GameView;
  artOk: boolean;
  /** Clickable spaces → the option id to answer with (and its label). */
  targets: Map<uf.SpaceId, { id: string; label: string }>;
  onPick: (optionId: string) => void;
}) {
  const spaces = Object.keys(kSpaceGeom) as uf.SpaceId[];
  const bySpace = new Map<uf.SpaceId, uf.PlayerView[]>();
  for (const p of view.players) bySpace.set(p.space, [...(bySpace.get(p.space) ?? []), p]);
  return (
    <svg className={css.miniMap} viewBox={`${kView.x} ${kView.y} ${kView.w} ${kView.h}`} preserveAspectRatio="xMidYMid meet">
      {artOk ? (
        <image href={art.board()} x={0} y={0} width={2000} height={1412} preserveAspectRatio="none" />
      ) : (
        <rect x={0} y={kView.y} width={2000} height={kView.h} fill="#0f2b30" />
      )}
      {spaces.map(sp => {
        const g = kSpaceGeom[sp];
        const t = targets.get(sp);
        const kind = uf.spaceKind(sp);
        const fill = artOk ? "transparent" : kind === "water" ? "#12343a" : kind === "deck" ? "#6b4a2b" : "#3d2a19";
        const [cx, cy] = polyCentroid(g.poly);
        return (
          <g key={sp} onClick={t ? () => onPick(t.id) : undefined} style={{ cursor: t ? "pointer" : "default" }}>
            <polygon points={polyPoints(g.poly)} fill={fill} stroke={artOk ? "transparent" : "#c9b27a"} strokeWidth={3} />
            {view.spaces[sp].damage && <polygon points={polyPoints(g.poly)} className={css.damaged} />}
            {t && <polygon points={polyPoints(g.poly)} className={css.miniTarget}><title>{t.label}</title></polygon>}
            {!artOk && kind !== "water" && (
              <text x={cx} y={cy + 10} textAnchor="middle" fontSize={kind === "interior" ? 34 : 26} fill="#e2cf9e" opacity={0.85} pointerEvents="none">{uf.spaceName(sp)}</text>
            )}
          </g>
        );
      })}
      {/* markers */}
      {spaces.flatMap(sp => {
        const [px, py] = kSpaceGeom[sp].people;
        const [mx, my] = kSpaceGeom[sp].monsters;
        const out: JSX.Element[] = [];
        (bySpace.get(sp) ?? []).forEach((p, i) => {
          const color = p.revealed ? "#c94040" : kSeatColors[p.seat % kSeatColors.length];
          out.push(
            <g key={`p${p.seat}`} transform={`translate(${px + (i - ((bySpace.get(sp)!.length - 1) / 2)) * 58},${py})`} pointerEvents="none">
              <circle r={26} fill="#0b1a1d" stroke={color} strokeWidth={7} />
              <text textAnchor="middle" dy={10} fontSize={28} fill={color} fontWeight={700}>{p.seat === view.you ? "★" : uf.kCharacters[p.character].name[0]}</text>
            </g>,
          );
        });
        const n = view.spaces[sp].deepOnes;
        if (n > 0) out.push(
          <g key={`d${sp}`} transform={`translate(${mx},${my})`} pointerEvents="none">
            <circle r={26} fill="#2f7a64" stroke="#bfe9d7" strokeWidth={4} />
            <text textAnchor="middle" dy={10} fontSize={28} fill="#fff" fontWeight={700}>{n}</text>
          </g>,
        );
        const ps = view.spaces[sp].passengers;
        if (ps > 0) out.push(
          <g key={`s${sp}`} transform={`translate(${mx - 64},${my})`} pointerEvents="none">
            <rect x={-22} y={-22} width={44} height={44} rx={8} fill="#e8dfc8" stroke="#6e5330" strokeWidth={3} />
            <text textAnchor="middle" dy={10} fontSize={28} fill="#1d140b" fontWeight={700}>{ps}</text>
          </g>,
        );
        return out;
      })}
    </svg>
  );
}

/** Options of a decision that name a space → map for the MiniMap. */
export function spaceTargets(options: uf.Option[]): Map<uf.SpaceId, { id: string; label: string }> {
  const m = new Map<uf.SpaceId, { id: string; label: string }>();
  for (const o of options) if (o.space && !m.has(o.space)) m.set(o.space, { id: o.id, label: o.label });
  return m;
}
